import { createContext, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { alertAPI } from '../API/alertAPI';

const initialState = {
  alerts: [],
  unresolvedCount: 0,
  isLoading: false,
  error: null,
};

const handlers = {
  SET_LOADING: (state, { payload }) => ({ ...state, isLoading: payload }),
  SET_ALERTS: (state, { payload }) => ({
    ...state,
    alerts: payload,
    unresolvedCount: payload.filter(a => !a.isResolved).length,
    isLoading: false,
  }),
  RESOLVE_ALERT: (state, { payload: id }) => {
    const alerts = state.alerts.map(a => (a.id === id ? { ...a, isResolved: true } : a));
    return { ...state, alerts, unresolvedCount: alerts.filter(a => !a.isResolved).length };
  },
  SET_ERROR: (state, { payload }) => ({ ...state, error: payload, isLoading: false }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AlertContext = createContext({ ...initialState });

export const AlertProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadAlertsByClient = useCallback(async (clientId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const data = await alertAPI.getUnresolvedByClient(clientId);
    if (data && !data.error) {
      dispatch({ type: 'SET_ALERTS', payload: data.value || [] });
    } else {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load alerts' });
    }
  }, []);

  const resolveAlert = useCallback(async (alertId) => {
    const data = await alertAPI.resolve(alertId);
    if (data && !data.error) {
      dispatch({ type: 'RESOLVE_ALERT', payload: alertId });
      toast.success('Alert resolved');
    } else {
      toast.error('Failed to resolve alert');
    }
  }, []);

  const bulkResolve = useCallback(async (alertIds) => {
    const data = await alertAPI.bulkResolve(alertIds);
    if (data && !data.error) {
      alertIds.forEach(id => dispatch({ type: 'RESOLVE_ALERT', payload: id }));
      toast.success(`${alertIds.length} alerts resolved`);
    } else {
      toast.error('Failed to resolve alerts');
    }
  }, []);

  return (
    <AlertContext.Provider value={{ ...state, loadAlertsByClient, resolveAlert, bulkResolve }}>
      {children}
    </AlertContext.Provider>
  );
};

AlertProvider.propTypes = { children: PropTypes.node.isRequired };

export default AlertContext;
